import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';

import { QueryKeys } from '@gokrew/models/constants';
import type { SignatureFileKind } from '@gokrew/models/signature-requests';
import { useEmployeePortalAuth } from '@/context/EmployeeAuthContext';
import {
  approveMySignatureApproval,
  continueMySignature,
  getMySignatureApproval,
  getMySignatureApprovals,
  requestChangesOnMySignatureApproval,
  getMySignatureAnswers,
  getMySignatureFile,
  getMySignatureRequests,
  openSignatureGate,
  submitMySignatureAnswers,
} from '@/api/signature-requests';

export function useMySignatureRequests() {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.MySignatureRequests, user?.id],
    queryFn: getMySignatureRequests,
    enabled: !!user,
  });
}

/** The nav badge and dashboard card: documents waiting for my signature. */
export function useMySignaturesToSign() {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.MySignatureRequests, user?.id],
    queryFn: getMySignatureRequests,
    enabled: !!user,
    refetchInterval: 60_000,
    select: (items) => items.filter((item) => item.bucket === 'to_sign').length,
  });
}

/** The gate's view of an emailed link. Never retried: a 404 is final. */
export function useSignatureGate(token?: string) {
  return useQuery({
    queryKey: [QueryKeys.SignatureGate, token],
    queryFn: () => openSignatureGate(token!),
    enabled: !!token,
    retry: false,
    staleTime: 0,
  });
}

/**
 * Opens my signing page as its own page (D75): the signing engine sends me
 * back to /sign/complete when done.
 */
export function useContinueSigning() {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (requestId: string) => continueMySignature(requestId),
    onSuccess: ({ url }) => {
      window.location.assign(url);
    },
    // The document asks me to fill fields in first (2026-10-10).
    onError: (error, requestId) => {
      const code = isAxiosError(error)
        ? (error.response?.data as { errorCode?: string } | undefined)
            ?.errorCode
        : undefined;
      if (code === 'ANSWERS_REQUIRED') {
        navigate(`/signatures/answers/${requestId}`);
      }
    },
  });
}

/** The fields I fill in before signing a document. */
export function useMySignatureAnswers(requestId?: string) {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.MySignatureAnswers, user?.id, requestId],
    queryFn: () => getMySignatureAnswers(requestId!),
    enabled: !!user && !!requestId,
    retry: false,
  });
}

/** Sends my answers, then opens my signing page as its own page. */
export function useSubmitSignatureAnswers() {
  return useMutation({
    mutationFn: ({
      requestId,
      answers,
    }: {
      requestId: string;
      answers: Record<string, unknown>;
    }) => submitMySignatureAnswers(requestId, answers),
    onSuccess: ({ url }) => {
      window.location.assign(url);
    },
  });
}

export function useOpenMySignatureFile() {
  return useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: SignatureFileKind }) =>
      getMySignatureFile(id, kind),
    onSuccess: ({ url }) => {
      window.open(url, '_blank', 'noopener,noreferrer');
    },
  });
}

/** Documents I approve before they go to an employee (2026-10-15). */
export function useMySignatureApprovals() {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.MySignatureApprovals, user?.id],
    queryFn: getMySignatureApprovals,
    enabled: !!user,
  });
}

export function useMySignatureApproval(requestId?: string) {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.MySignatureApprovals, user?.id, requestId],
    queryFn: () => getMySignatureApproval(requestId!),
    enabled: !!user && !!requestId,
    retry: false,
  });
}

/** Approve and send, or send back with a comment; both refresh my lists. */
export function useDecideSignatureApproval() {
  const queryClient = useQueryClient();
  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.MySignatureApprovals],
    });
  return {
    approve: useMutation({
      mutationFn: (requestId: string) => approveMySignatureApproval(requestId),
      onSuccess: refresh,
    }),
    requestChanges: useMutation({
      mutationFn: ({ id, comment }: { id: string; comment: string }) =>
        requestChangesOnMySignatureApproval(id, comment),
      onSuccess: refresh,
    }),
  };
}
